import React, { useEffect, useMemo, useRef, useState } from "react";
import { Link, NavLink, useLocation } from "react-router-dom";
import { Cookie } from "lucide-react";
import { useAuth } from "../contexts/AuthContext";
import { AccountMenu } from "./AccountMenu";
import ModeToggle from "./mode-toggle";
import MobileSideMenu from "./MobileSideMenu";
import { Button } from "@/components/ui/button";
import {
  DropdownMenu,
  DropdownMenuTrigger,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
} from "@/components/ui/dropdown-menu";
import useMediaQuery from "../hooks/useMediaQuery";
import { useTheme } from "./theme-provider";
import useCurrentUserRoles from "@/features/users/hooks/useCurrentUserRoles";
import { ResourcesMenu } from "./ResourcesMenu";

const ADMIN_TOOL_PATHS = ["/integrations", "/statuses", "/users", "/apps", "/licenses", "/notifications", "/ai-settings"];
const GLOBAL_ADMIN_ONLY_ADMIN_TOOL_PATHS = ["/integrations", "/statuses", "/apps", "/ai-settings"];

export type NavigationLink = {
  to: string;
  label: string;
  requiresAuth?: boolean;
};

export const NAV_LINKS: NavigationLink[] = [
  { to: "/extensions", label: "Extensions", requiresAuth: true },
  { to: "/integrations", label: "Integrations", requiresAuth: true },
  { to: "/statuses", label: "Statuses", requiresAuth: true },
  { to: "/extensions/metrics", label: "Dashboard", requiresAuth: true },
  { to: "/users", label: "Users", requiresAuth: true },
  { to: "/organizations", label: "Organizations", requiresAuth: true },
  { to: "/apps", label: "Apps", requiresAuth: true },
  { to: "/licenses", label: "Licenses", requiresAuth: true },
  { to: "/notifications", label: "Notifications", requiresAuth: true },
  { to: "/ai-settings", label: "AI settings", requiresAuth: true },
];

const Navbar: React.FC = () => {
  const { isAuthenticated, isLoading, login } = useAuth();
  const { canViewAdminTools, isGlobalAdmin } = useCurrentUserRoles({ enabled: isAuthenticated });
  const location = useLocation();
  const { resolvedTheme } = useTheme();
  const [menuOpen, setMenuOpen] = useState(false);
  const [isStartingLogin, setIsStartingLogin] = useState(false);
  const menuRef = useRef<HTMLDivElement | null>(null);
  const mobileMenuRef = useRef<HTMLDivElement | null>(null);
  const isDesktop = useMediaQuery("(min-width: 1024px)");
  const isAuthInProgress = !isAuthenticated && isLoading;
  const signInReturnTo = `${location.pathname}${location.search}${location.hash}` || "/";

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      const target = event.target as Node | null;
      if (!target) return;

      if (menuRef.current && menuRef.current.contains(target)) {
        return;
      }

      if (!isDesktop && mobileMenuRef.current && mobileMenuRef.current.contains(target)) {
        return;
      }

      setMenuOpen(false);
    };

    document.addEventListener("mousedown", handleClickOutside);

    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, [isDesktop]);

  useEffect(() => {
    if (isDesktop) {
      setMenuOpen(false);
    }
  }, [isDesktop]);

  const handleLogout = () => {
    setMenuOpen(false);
  };

  const handleLogin = async () => {
    if (isLoading || isStartingLogin) {
      return;
    }

    setMenuOpen(false);
    setIsStartingLogin(true);

    try {
      await login(signInReturnTo);
    } catch (error) {
      console.error("Navbar login redirect failed:", error);
      setIsStartingLogin(false);
    }
  };

  // initials moved into AccountMenu component

  const navLinks = useMemo(
    () =>
      NAV_LINKS.filter((link) => {
        if (link.requiresAuth && !isAuthenticated) {
          return false;
        }

        if (!ADMIN_TOOL_PATHS.includes(link.to)) {
          return true;
        }

        if (!canViewAdminTools) {
          return false;
        }

        if (GLOBAL_ADMIN_ONLY_ADMIN_TOOL_PATHS.includes(link.to) && !isGlobalAdmin) {
          return false;
        }

        return true;
      }),
    [canViewAdminTools, isAuthenticated, isGlobalAdmin],
  );

  const adminToolLinks = useMemo(() => navLinks.filter((link) => ADMIN_TOOL_PATHS.includes(link.to)), [navLinks]);

  const navLinkClassName = ({ isActive }: { isActive: boolean }) =>
    [
      "relative rounded-md px-3 py-2 text-sm tracking-wide transition-colors",
      isActive
        ? "font-semibold text-foreground-light after:absolute after:bottom-0 after:left-0 after:right-0 after:h-[2px] after:bg-brand-secondary after:content-[''] dark:text-foreground-dark"
        : "font-medium text-foreground-muted-light hover:text-brand-secondary-light dark:text-foreground-muted-dark dark:hover:text-brand-secondary-light",
    ].join(" ");

  return (
    <nav className="relative z-30 border-b border-brand-secondary-dark bg-[#E6E6E6] shadow-[0_2px_6px_rgba(0,0,0,0.4)] dark:bg-[#1B1E1F]">
      <div className="mx-auto flex h-16 max-w-screen-xl items-center justify-between px-4 sm:px-6 lg:px-8">
        <div className="flex items-center gap-2 sm:gap-4">
          {/* Mobile hamburger */}
          {!isDesktop && (
            <Button type="button" size="icon" variant="ghost" aria-label="Open menu" aria-expanded={menuOpen} onClick={() => setMenuOpen(true)}>
              <svg className="h-5 w-5" viewBox="0 0 20 20" fill="currentColor" aria-hidden="true">
                <path d="M3 6.25A.75.75 0 0 1 3.75 5.5h12.5a.75.75 0 0 1 0 1.5H3.75A.75.75 0 0 1 3 6.25Zm0 4c0-.414.336-.75.75-.75h12.5a.75.75 0 0 1 0 1.5H3.75a.75.75 0 0 1-.75-.75Zm.75 3.25a.75.75 0 0 0 0 1.5h12.5a.75.75 0 0 0 0-1.5H3.75Z" />
              </svg>
            </Button>
          )}
          <Link to="/" prefetch="intent" aria-label="Home" className="flex items-center">
            <img
              src={resolvedTheme === "dark" ? "/img/assistant_icon_dark_96.png" : "/img/assistant_icon_light_96.png"}
              alt="Assistant Portal home"
              className="h-8 w-8"
            />
          </Link>
          {isDesktop && (
            <div className="flex items-center gap-1">
              {/**
               * Keep Extensions, Dashboard, and Organizations as top-level items.
               * Group admin-only pages under Admin tools.
               */}
              {navLinks
                .filter((l) => l.to === "/extensions" || l.to === "/extensions/metrics" || l.to === "/organizations")
                .map(({ to, label }) => (
                  <NavLink key={to} to={to} prefetch="intent" end={to === "/extensions"} className={navLinkClassName}>
                    {label}
                  </NavLink>
                ))}

              {/* Admin tools dropdown */}
              {isAuthenticated && adminToolLinks.length > 0 && (
                <DropdownMenu>
                  <DropdownMenuTrigger asChild>
                    <button className="relative rounded-md px-3 py-2 text-sm font-medium text-foreground-muted-light hover:text-brand-secondary-light dark:text-foreground-muted-dark dark:hover:text-brand-secondary-light">
                      Admin tools
                    </button>
                  </DropdownMenuTrigger>
                  <DropdownMenuContent align="start">
                    <DropdownMenuLabel>Admin tools</DropdownMenuLabel>
                    <DropdownMenuSeparator />
                    {adminToolLinks.map(({ to, label }) => (
                      <DropdownMenuItem key={to} asChild>
                        <Link to={to} prefetch="intent">
                          {label}
                        </Link>
                      </DropdownMenuItem>
                    ))}
                  </DropdownMenuContent>
                </DropdownMenu>
              )}

              <ResourcesMenu />
            </div>
          )}
        </div>

        <div className="flex items-center gap-3 text-foreground-light dark:text-foreground-dark">
          {isAuthenticated && (
            <Button asChild variant="default" size="sm" className="hidden sm:inline-flex">
              <Link to="/getting-started">Download now!</Link>
            </Button>
          )}

          {isDesktop && (
            <Button
              variant="outline"
              size="icon"
              onClick={() => window.dispatchEvent(new CustomEvent("cookiekit:open-preferences"))}
              aria-label="Cookie settings"
              className="inline-flex"
            >
              <Cookie className="h-[1.2rem] w-[1.2rem]" />
              <span className="sr-only">Cookie settings</span>
            </Button>
          )}
          <ModeToggle />

          {isAuthenticated ? (
            <div ref={menuRef}>
              <AccountMenu onLogout={handleLogout} />
            </div>
          ) : isAuthInProgress || isStartingLogin ? (
            <Button variant="outline" disabled className="rounded-full px-4 py-2 text-sm font-semibold">
              <span className="inline-block h-4 w-4 animate-spin rounded-full border-2 border-current border-r-transparent" aria-hidden="true" />
              <span className="ml-2">Signing in...</span>
            </Button>
          ) : (
            <Button type="button" variant="outline" onClick={() => void handleLogin()}>
              <span className="rounded-full px-4 py-2 text-sm font-semibold">Sign in</span>
            </Button>
          )}
        </div>
      </div>
      {/* Mobile side menu */}
      {!isDesktop && (
        <div>
          <MobileSideMenu
            open={menuOpen}
            onClose={() => setMenuOpen(false)}
            onPanelMount={(node) => {
              mobileMenuRef.current = node;
            }}
            links={navLinks}
            isAuthenticated={isAuthenticated}
          />
        </div>
      )}
    </nav>
  );
};

export default Navbar;
